import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Aurora } from "@/components/motion/aurora";
import { FadeIn } from "@/components/motion/fade-in";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Billing = "monthly" | "yearly";

interface Plan {
  name: string;
  description: string;
  highlight?: boolean;
  monthly: number | "free" | "custom";
  yearly: number | "free" | "custom";
  cta: { label: string; to: string };
  features: string[];
}

const PLANS: Plan[] = [
  {
    name: "Free",
    description: "For personal use. All features, lower throughput.",
    monthly: "free",
    yearly: "free",
    cta: { label: "Start free", to: "/signup" },
    features: [
      "1 project",
      "Unlimited tools",
      "Unlimited tool calls",
      "All 319+ integrations",
      "100 MB memory · 1 store",
      "100k vectors · 1 index",
      "Multi-protocol tool export",
      "Private MCP servers",
      "Skill imports (skills.sh)",
      "10 tool calls / minute",
      "Community support",
    ],
  },
  {
    name: "Pro",
    description: "For builders shipping real AI agents.",
    monthly: 5,
    yearly: 48,
    highlight: true,
    cta: { label: "Start Pro trial", to: "/signup" },
    features: [
      "Everything in Free",
      "Unlimited projects",
      "5 GB memory · 10 stores",
      "1M vectors · 10 indexes",
      "1,000 tool calls / minute",
      "100× the Free throughput",
      "Priority support",
      "Early access to new features",
    ],
  },
  {
    name: "Scale",
    description: "For production workloads with serious traffic.",
    monthly: 20,
    yearly: 192,
    cta: { label: "Start Scale trial", to: "/signup" },
    features: [
      "Everything in Pro",
      "50 GB memory · unlimited stores",
      "10M vectors · unlimited indexes",
      "10,000 tool calls / minute",
      "10× the Pro throughput",
      "Usage analytics + alerts",
      "Audit logs (1-year retention)",
    ],
  },
  {
    name: "Enterprise",
    description: "For teams with custom needs.",
    monthly: "custom",
    yearly: "custom",
    cta: { label: "Talk to sales", to: "mailto:sales@memkernel.com" },
    features: [
      "Everything in Scale",
      "Custom memory + vectors",
      "Self-host on your infra",
      "SSO + audit logs",
      "Dedicated agent runtime",
      "Custom integrations",
      "Annual contract + SLA",
      "Solutions architect",
    ],
  },
];

const FAQ = [
  {
    q: "What counts as a tool call?",
    a: "Every time an agent invokes a tool through MemKernel — search Drive, send an email, run a SQL query, hit a custom HTTP endpoint — that's one tool call. Total tool calls per month are unlimited on every plan; only the per-minute rate is capped. Free is 10/min (great for hobby agents), Pro is 1,000/min (real production for personal projects), Scale is 10,000/min (production at any traffic).",
  },
  {
    q: "Why is Free so generous?",
    a: "Because the value comes from running real agents through MemKernel — not from a 14-day trial. Free covers every feature: 1 project with unlimited tools, unlimited tool calls per month, every integration, memory + vector, multi-protocol export, skills. The per-minute throughput is capped at 10/min and you're limited to one project, which keeps Free workable for a personal assistant or a hobby agent — and routes serious production work to Pro or Scale.",
  },
  {
    q: "Pro vs Scale — how do I pick?",
    a: "Pick Pro ($5/mo, 1,000 tool calls/min) if you're an individual builder shipping personal agents — that's enough rate headroom for one human-scale agent running flat-out. Pick Scale ($20/mo, 10,000 tool calls/min) if you're running production agents that serve other users (a Slack bot for your team, a customer-facing assistant, a scheduled scraper that fans out). You can upgrade from Pro to Scale anytime; we prorate.",
  },
  {
    q: "What's the difference between a memory store and a vector index?",
    a: "A memory store is typed key/value (or small documents) — facts, preferences, conversation summaries, thread state. A vector index is the embedded-and-searchable form of a corpus — a folder of PDFs, a Notion database, a website. You'd write a user's tone preference to memory; you'd embed your product handbook into a vector index. Both are project-scoped.",
  },
  {
    q: "Why limit indexes when I already pay for vectors?",
    a: "An index is a named, separately-searchable bucket. A query against one index never bleeds into another. Multi-tenant apps want one index per customer (so customer A's tickets never surface in customer B's search). Personal apps usually only need one. The Scale tier removes the index cap so multi-tenant builders aren't hemmed in.",
  },
  {
    q: "What if I blow past my memory or vector limit?",
    a: "Writes start returning a 402 quota_exceeded error; reads keep working. You'll get an email and an in-dashboard banner at 80% so it doesn't surprise you. Upgrade is one click and prorates within the current billing period.",
  },
  {
    q: "Is MemKernel really personal-account only?",
    a: "Yes. We don't have multi-user workspaces — most upstream OAuth providers (Gmail, Drive, GitHub personal, Slack personal) are personal-scoped, so a shared workspace would leak more than it shares. Enterprise customers can talk to us about custom team setups.",
  },
  {
    q: "Do I need an AI model API key?",
    a: "No. MemKernel is infrastructure, not an LLM provider. You bring your own model — Claude, ChatGPT, Cursor, or your own — and we sit underneath as the tool, memory, and identity layer.",
  },
  {
    q: "What's the MCP server thing?",
    a: "Model Context Protocol is the open standard agent clients use to talk to tools. Each MemKernel project exposes one MCP URL; paste it into Claude Desktop or Cursor and your project's tools are immediately available.",
  },
  {
    q: "Can I self-host?",
    a: "Yes. MemKernel is open source. Single Docker image, Postgres for state, optional Redis for rate limiting. See the self-hosting docs.",
  },
  {
    q: "When does the agent runtime ship?",
    a: "Phase 3 of the roadmap, post-MVP. We're focused on getting the connect-tools-once-expose-everywhere wedge right first.",
  },
];

export function Pricing() {
  const [billing, setBilling] = useState<Billing>("yearly");

  return (
    <div>
      <section className="relative isolate overflow-hidden border-b border-border/40 py-20 sm:py-24">
        <Aurora className="opacity-50" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeIn immediate className="mx-auto max-w-3xl text-center">
            <Badge variant="secondary" className="mb-4">
              Pricing
            </Badge>
            <h1 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl">
              Simple, transparent pricing
            </h1>
            <p className="mt-4 text-muted-foreground sm:text-lg">
              Every plan gets every feature. The only difference is throughput
              — how many tool calls you can fire per minute. Cancel any time.
            </p>
          </FadeIn>

          <FadeIn immediate delay={0.1}>
            <div className="mt-10 flex justify-center">
              <div className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/80 p-1 backdrop-blur">
                <BillingToggle
                  label="Monthly"
                  active={billing === "monthly"}
                  onClick={() => setBilling("monthly")}
                />
                <BillingToggle
                  label="Yearly · save 20%"
                  active={billing === "yearly"}
                  onClick={() => setBilling("yearly")}
                />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {PLANS.map((plan, idx) => {
              const price = billing === "monthly" ? plan.monthly : plan.yearly;
              return (
                <FadeIn key={plan.name} delay={idx * 0.07}>
                  <div
                    className={cn(
                      "relative flex h-full flex-col rounded-3xl border bg-card p-8",
                      plan.highlight
                        ? "border-foreground/40 shadow-2xl shadow-foreground/10"
                        : "border-border/60",
                    )}
                  >
                    {plan.highlight && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1">
                        Most popular
                      </Badge>
                    )}
                    <h3 className="text-2xl font-semibold">{plan.name}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {plan.description}
                    </p>
                    <div className="mt-6 flex items-baseline gap-1">
                      {price === "free" ? (
                        <span className="text-4xl font-bold">$0</span>
                      ) : price === "custom" ? (
                        <span className="text-4xl font-bold">Custom</span>
                      ) : (
                        <>
                          <span className="text-4xl font-bold">
                            ${billing === "monthly" ? price : Math.round((price as number) / 12)}
                          </span>
                          <span className="text-muted-foreground">/month</span>
                        </>
                      )}
                    </div>
                    {billing === "yearly" && typeof price === "number" && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Billed ${price} annually
                      </p>
                    )}

                    <Button
                      asChild
                      className="mt-6"
                      variant={plan.highlight ? "default" : "outline"}
                      size="lg"
                    >
                      {plan.cta.to.startsWith("mailto:") ? (
                        <a href={plan.cta.to}>{plan.cta.label}</a>
                      ) : (
                        <Link to={plan.cta.to}>{plan.cta.label}</Link>
                      )}
                    </Button>

                    <ul className="mt-8 space-y-3 text-sm">
                      {plan.features.map((feat) => (
                        <li key={feat} className="flex items-start gap-2">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-border/40 py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <FadeIn className="mb-10 text-center">
            <h2 className="text-3xl font-bold tracking-tight">FAQ</h2>
          </FadeIn>
          <div className="space-y-4">
            {FAQ.map((item, idx) => (
              <FadeIn key={item.q} delay={idx * 0.04}>
                <details className="group rounded-xl border border-border/60 bg-card p-6 open:shadow-md">
                  <summary className="flex cursor-pointer list-none items-center justify-between font-semibold">
                    {item.q}
                    <span className="ml-4 inline-block transition-transform group-open:rotate-180">
                      ↓
                    </span>
                  </summary>
                  <p className="mt-3 text-sm text-muted-foreground">{item.a}</p>
                </details>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function BillingToggle({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "relative rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
        active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {active && (
        <motion.span
          layoutId="billing-pill"
          className="absolute inset-0 -z-10 rounded-full bg-foreground/10"
          transition={{ type: "spring", duration: 0.4, bounce: 0.2 }}
        />
      )}
      {label}
    </button>
  );
}
